/**
 * The one date / time formatter for WRAP screens (V2-073, V2-021; DESIGN.md FMT-01 to FMT-08,
 * FMT-24). No screen formats a date on its own.
 *
 *   formatDate        2026-10-13
 *   formatDateTime    2026-06-11 1:16 PM
 *   formatTime        1:16 PM
 *   formatDateRange   2026-10-05 – 2026-10-11
 *   formatWeekdayDate Tue 2026-10-13   (Schedule chart column headers only, FMT-06)
 *
 * Instants (timestamps) are shown in Pacific Time, America/Vancouver, whatever the device's own
 * time zone. Date-only values – a `YYYY-MM-DD` string, or a timestamp at exactly midnight UTC,
 * which is how DATE columns arrive from the API – are calendar dates and are never shifted: the
 * task that starts on 2026-04-22 shows 2026-04-22 everywhere.
 *
 * Not for protected report output (MOTT, Payroll, Finance – RPT-06), which keeps its own format.
 */

export const PACIFIC_TIME_ZONE = 'America/Vancouver';

export type DateInput = string | number | Date | null | undefined;

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const UTC_MIDNIGHT_RE = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.0+)?(?:Z|[+-]00:?00)$/;

const ymdFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: PACIFIC_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const timeFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: PACIFIC_TIME_ZONE,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A calendar date carried by the value itself, if it is date-only. */
function dateOnlyParts(value: DateInput): [number, number, number] | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const midnightUtc =
      value.getUTCHours() === 0 && value.getUTCMinutes() === 0 && value.getUTCSeconds() === 0 && value.getUTCMilliseconds() === 0;
    return midnightUtc ? [value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate()] : null;
  }
  if (typeof value !== 'string') return null;
  const m = value.trim().match(DATE_ONLY_RE) ?? value.trim().match(UTC_MIDNIGHT_RE);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

function toInstant(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DD`, or '' when there is no valid date (render the shared EmptyValue for ''). */
export function formatDate(value: DateInput): string {
  const parts = dateOnlyParts(value);
  if (parts) return `${parts[0]}-${pad(parts[1])}-${pad(parts[2])}`;
  const d = toInstant(value);
  return d ? ymdFormat.format(d) : '';
}

/** `h:mm AM` / `h:mm PM` in Pacific Time; '' for a date-only value or no value. */
export function formatTime(value: DateInput): string {
  if (dateOnlyParts(value)) return '';
  const d = toInstant(value);
  return d ? timeFormat.format(d).replace(/\s+/g, ' ').replace(/([ap])\.?m\.?$/i, (_m, x: string) => `${x.toUpperCase()}M`) : '';
}

/** `YYYY-MM-DD h:mm AM`. A date-only value shows the date alone (FMT-24: no midnight time). */
export function formatDateTime(value: DateInput): string {
  const date = formatDate(value);
  if (!date) return '';
  const time = formatTime(value);
  return time ? `${date} ${time}` : date;
}

/** `2026-10-05 – 2026-10-11` (spaced en dash, FMT-08). A missing end shows the start alone. */
export function formatDateRange(start: DateInput, end: DateInput): string {
  const a = formatDate(start);
  const b = formatDate(end);
  if (a && b) return a === b ? a : `${a} – ${b}`;
  return a || b;
}

/** `Tue 2026-10-13` – Schedule chart column headers only (FMT-06). */
export function formatWeekdayDate(value: DateInput): string {
  const ymd = formatDate(value);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return `${WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${ymd}`;
}

/** Today's calendar date in Pacific Time, `YYYY-MM-DD`. */
export function todayPacific(now: Date = new Date()): string {
  return ymdFormat.format(now);
}
